import type { FixtureDescriptor } from "./fixtures";

export function stackId(fixture: FixtureDescriptor): string | undefined {
  return fixture.scope.stack.role === "standalone" ? undefined : fixture.scope.stack.id;
}

export function stackMembers(fixture: FixtureDescriptor, fixtures: FixtureDescriptor[]): FixtureDescriptor[] {
  const id = stackId(fixture);
  if (!id) return [fixture];
  return fixtures
    .filter((candidate) => stackId(candidate) === id)
    .sort((left, right) => left.scope.stack.position - right.scope.stack.position);
}

export function nextStackMember(
  fixture: FixtureDescriptor | undefined,
  fixtures: FixtureDescriptor[],
  completedIds: ReadonlySet<string>,
): FixtureDescriptor | undefined {
  if (!fixture) return undefined;
  return stackMembers(fixture, fixtures).find((candidate) =>
    candidate.scope.stack.position > fixture.scope.stack.position
      && !completedIds.has(candidate.id)
      && candidate.reviewable !== false,
  );
}

export function stackPrerequisitesSatisfied(
  fixture: FixtureDescriptor,
  fixtures: FixtureDescriptor[],
  completedIds: ReadonlySet<string>,
): boolean {
  const reviewAfter = fixture.scope.stack.reviewAfter ?? [];
  if (reviewAfter.length === 0) return true;
  const byPull = new Map(fixtures.map((candidate) => [candidate.pullNumber, candidate]));
  return reviewAfter.every((pullNumber) => {
    const prerequisite = byPull.get(pullNumber);
    // Hosted queues already apply live GitHub dependency status. Only locally
    // known stack parts need a browser-session completion check here.
    return !prerequisite || completedIds.has(prerequisite.id);
  });
}

export function stackContextStorageKey(repository: string, id: string, revision = "legacy"): string {
  return `chew:stack-context:${repository}:${id}:${revision}`;
}

export function hasSeenStackContext(fixture: FixtureDescriptor, revision?: string): boolean {
  const id = stackId(fixture);
  return Boolean(id && localStorage.getItem(stackContextStorageKey(fixture.repository, id, revision)) === "seen");
}

export function rememberStackContext(fixture: FixtureDescriptor, revision?: string): void {
  const id = stackId(fixture);
  if (id) localStorage.setItem(stackContextStorageKey(fixture.repository, id, revision), "seen");
}
